// src/layouts/AdminLayout.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LogOut, Home, PackageCheck, Menu, PackageOpen, Truck, UserCog, MonitorCog,
  FileText, ShieldCheck, UserPlus, CircleDollarSign, UserCircle
} from "lucide-react";
import { useAdmin } from "../context/AdminContext";
import axios from "axios";

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const { admin, setAdmin, rolePermissions, loading, isSuperAdmin } = useAdmin();
  const permissions = rolePermissions;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // Redirect to login if admin is null after loading
  useEffect(() => {
    if (!loading && !admin) {
      navigate("/admin-login");
    }
  }, [loading, admin, navigate]);

  // Close sidebar on outside click (mobile)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSidebarOpen]);

  const handleLogout = async () => {
    try {
      await axios.post(`${BASE_URL}/api/admin/logout`, null, {
        withCredentials: true,
      });
      setAdmin(null);
      navigate("/admin-login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const isActive = (path) => location.pathname === path;
  const canAccess = (module) => permissions.includes(module);
  
  return (
    <div className="flex relative overflow-hidden min-h-full">
      
      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          bg-gray-800 text-white z-50 transform top-0 left-0 fixed md:static transition-transform duration-200 ease-in-out
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
          w-64`}
      >
        <div className="h-screen md:min-h-full md:h-auto overflow-y-auto p-6">
          <div className="mb-8 flex border-b border-gray-500 pb-4">
            <img
              src="/images/logos/Exudee-logo-white.svg"
              alt="Logo"
              className="h-14 w-auto"
            />
          </div>

          <h2 className="text-xl font-bold mb-6 mt-3">Admin Panel</h2>

          <nav className="flex flex-col gap-3">
            {canAccess("dashboard") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/dashboard") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/dashboard");
                  setIsSidebarOpen(false);
                }}
              >
                <Home className="w-5 h-5" />
                Dashboard
              </button>
            )}

            {canAccess("products") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/products") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/products");
                  setIsSidebarOpen(false);
                }}
              >
                <PackageOpen className="w-5 h-5" />
                Products
              </button>
            )}

            {canAccess("orders") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/orders") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/orders");
                  setIsSidebarOpen(false);
                }}
              >
                <PackageCheck className="w-5 h-5" />
                Orders
              </button>
            )}

            {canAccess("drivers") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/drivers") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/drivers");
                  setIsSidebarOpen(false);
                }}
              >
                <Truck className="w-5 h-5" />
                Drivers
              </button>
            )}

            {canAccess("blogs") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/blogs") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/blogs");
                  setIsSidebarOpen(false);
                }}
              >
                <FileText className="w-5 h-5" />
                Blogs
              </button>
            )}

            {canAccess("users") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/users") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/users");
                  setIsSidebarOpen(false);
                }}
              >
                <UserCog className="w-5 h-5" />
                User Mgt.
              </button>
            )}

            {canAccess("finance") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/finance") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/finance");
                  setIsSidebarOpen(false);
                }}
              >
                <CircleDollarSign className="w-5 h-5" />
                Finance Mgt.
              </button>
            )}

            {canAccess("site-settings") && (
              <button
                className={`flex items-center gap-2 p-2 rounded ${
                  isActive("/admin/site-settings") ? "bg-gray-700" : "hover:bg-gray-700"
                }`}
                onClick={() => {
                  navigate("/admin/site-settings");
                  setIsSidebarOpen(false);
                }}
              >
                <MonitorCog className="w-5 h-5" />
                Site Settings
              </button>
            )}

            {isSuperAdmin && (
              <>
                <button
                  className={`flex items-center gap-2 p-2 rounded ${
                    isActive("/admin/roles") ? "bg-gray-700" : "hover:bg-gray-700"
                  }`}
                  onClick={() => {
                    navigate("/admin/roles");
                    setIsSidebarOpen(false);
                  }}
                >
                  <ShieldCheck className="w-5 h-5" />
                  Roles Manager
                </button>

                <button
                  className={`flex items-center gap-2 p-2 rounded ${
                    isActive("/admin/create") ? "bg-gray-700" : "hover:bg-gray-700"
                  }`}
                  onClick={() => {
                    navigate("/admin/create");
                    setIsSidebarOpen(false);
                  }}
                >
                  <UserPlus className="w-5 h-5" />
                  Create Admin
                </button>
              </>
            )}

            <button
              className="flex items-center gap-2 p-2 hover:bg-gray-700 rounded"
              onClick={handleLogout}
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          </nav>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 bg-gray-100 min-h-screen overflow-x-auto">
        {/* Top Navbar */}
        <div className="flex justify-between md:justify-end items-center bg-white border-b p-4 shadow-sm sticky top-0 z-40">
          {/* Admin dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="flex items-center gap-2 pr-3 text-gray-700 hover:text-black"
            >
              <UserCircle size={30} />
              <span className="font-medium">Admin</span>
            </button>
            {showDropdown && (
              <div className="absolute left-0 md:right-0 mt-2 w-40 bg-white rounded shadow z-50 border">
                <button
                  onClick={() => {
                    navigate("/admin/profile");
                    setShowDropdown(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm flex items-center gap-2"
                >
                  <UserCircle size={16} /> Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm text-red-600 flex items-center gap-2"
                >
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="bg-gray-800 text-white p-2 rounded shadow"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
